import { useEffect, useState, useMemo } from "react";
import { supabase } from "../supabaseClient";
import { useNavigate } from "react-router-dom";
import { Newspaper, Pencil, Trash2, PlusCircle, Search } from "lucide-react";
import i18n from "../i18n";
import { LogoLoader } from "../components/LogoLoader"; // 👈 AJOUT

/* ===== Types (multilingue + rétro-compat) ===== */
type NewsItem = {
  id: string;
  created_at: string;

  // legacy
  title: string | null;
  content: string | null;

  // nouveaux champs
  title_fr?: string | null;
  content_fr?: string | null;
  title_en?: string | null;
  content_en?: string | null;
};

/* ===== i18n mini-hook (pattern CreateTeam) ===== */
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null
      ? String((i18n.t as any)(key, options))
      : String((i18n.t as any)(key));
  return { t, lng };
}

/* ===== Helper : sélection du contenu selon langue, avec fallbacks ===== */
function pickLocalized(n: NewsItem, lng: string) {
  if (lng === "fr") {
    return {
      title: n.title_fr ?? n.title_en ?? n.title ?? "",
      content: n.content_fr ?? n.content_en ?? n.content ?? "",
    };
  }
  // en par défaut
  return {
    title: n.title_en ?? n.title_fr ?? n.title ?? "",
    content: n.content_en ?? n.content_fr ?? n.content ?? "",
  };
}

export const News = () => {
  const { t, lng } = useI18nSafe();

  const [newsList, setNewsList] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  const adminEmail = "cyprien.conseillon@icloud.com";

  useEffect(() => {
    const fetchAll = async () => {
      const [{ data: auth }, { data: newsData, error }] = await Promise.all([
        supabase.auth.getUser(),
        supabase
          .from("news")
          .select(
            "id, created_at, title, content, title_fr, content_fr, title_en, content_en"
          )
          .order("created_at", { ascending: false }),
      ]);

      setUserEmail(auth?.user?.email ?? null);

      if (error) {
        console.error("Erreur chargement des news :", error);
      } else {
        setNewsList((newsData as NewsItem[]) || []);
      }
      setLoading(false);
    };

    fetchAll();
  }, []);

  const handleDelete = async (id: string) => {
    if (!window.confirm(t("news:confirm_delete"))) return;
    const { error } = await supabase.from("news").delete().eq("id", id);
    if (error) {
      console.error(error);
      alert(t("news:delete_error"));
      return;
    }
    setNewsList((prev) => prev.filter((n) => n.id !== id));
  };

  /* ===== Recherche : on cherche dans TOUTES les variantes (fr/en/legacy) ===== */
  const filteredNews = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return newsList;
    return newsList.filter((n) => {
      const haystack =
        [
          n.title,
          n.content,
          n.title_fr,
          n.content_fr,
          n.title_en,
          n.content_en,
        ]
          .filter(Boolean)
          .join(" \n ")
          .toLowerCase() || "";
      return haystack.includes(q);
    });
  }, [newsList, searchTerm]);

  const locale = lng === "fr" ? "fr-FR" : "en-US";

  return (
    <section className="p-6 max-w-4xl mx-auto min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-orange-600 dark:text-orange-400 flex items-center gap-2">
          <Newspaper className="w-8 h-8" />
          {t("news:title")}
        </h1>

        {userEmail === adminEmail && (
          <button
            onClick={() => navigate("/createnews")}
            className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold px-3 py-2 rounded
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2
                       dark:focus-visible:ring-offset-gray-950 transition-colors"
          >
            <PlusCircle className="w-5 h-5" />
            {t("news:actions.add")}
          </button>
        )}
      </div>

      {loading ? (
        <LogoLoader /> // 👈 remplacé le texte par le loader
      ) : newsList.length === 0 ? (
        <p className="text-center text-gray-700 dark:text-gray-400">
          {t("news:empty")}
        </p>
      ) : (
        <>
          {/* Recherche */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              aria-label={t("news:search.aria")}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("news:search.placeholder")}
              className="w-full pl-9 pr-3 p-2 rounded text-sm border border-gray-300 dark:border-gray-700
                         bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100
                         placeholder-gray-400 dark:placeholder-gray-500
                         focus:outline-none focus:ring-2 focus:ring-orange-400 transition-colors"
            />
          </div>

          {filteredNews.length === 0 ? (
            <p className="text-center text-gray-700 dark:text-gray-400">
              {t("news:no_results", { term: searchTerm })}
            </p>
          ) : (
            <div className="space-y-6">
              {filteredNews.map((news) => {
                const { title, content } = pickLocalized(news, lng);
                return (
                  <article
                    key={news.id}
                    className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4 rounded shadow-sm
                               hover:shadow transition-shadow"
                  >
                    <h2 className="text-xl font-semibold mb-1 text-gray-900 dark:text-gray-100">
                      {title}
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                      {new Date(news.created_at).toLocaleDateString(locale, {
                        day: "2-digit",
                        month: "long",
                        year: "numeric",
                      })}
                    </p>
                    <p className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                      {content}
                    </p>

                    {/* Boutons admin uniquement */}
                    {userEmail === adminEmail && (
                      <div className="flex gap-4 mt-4">
                        <button
                          onClick={() => navigate(`/editnews/${news.id}`)}
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700
                                     dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium
                                     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded px-1"
                        >
                          <Pencil className="w-4 h-4" />
                          {t("news:actions.edit")}
                        </button>
                        <button
                          onClick={() => handleDelete(news.id)}
                          className="inline-flex items-center gap-1 text-red-600 hover:text-red-700
                                     dark:text-red-400 dark:hover:text-red-300 text-sm font-medium
                                     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 rounded px-1"
                        >
                          <Trash2 className="w-4 h-4" />
                          {t("news:actions.delete")}
                        </button>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </>
      )}
    </section>
  );
};
